import is from '@sindresorhus/is';
import { createElement, type FunctionComponent, type PropsWithChildren, type ReactNode } from 'react';

import {
    getJobArchitectureUrlTileChatContext,
    jobArchitectureUrlTileChatTypes,
    jobArchitectureUrlTileModule,
} from '~/feature/job-architecture-v2';
import {
    LEARNING_OPPORTUNITY_CREATE_TILE_TYPE,
    LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE,
    LEARNING_OPPORTUNITY_EDIT_TILE_TYPE,
    learningOpportunityUrlTileModule,
} from '~/feature/learning-opportunities-v2';
import { SKILL_DETAIL_TILE_TYPE, skillDetailUrlTileModule } from '~/feature/skills';

import { createChatUrlTileChrome } from '../chat';
import type { TilePermissionResolvers } from './availability/types';
import type { UrlTileChromeRenderer, UrlTileDefinition, UrlTileLegacyRewriter, UrlTileModule } from './types';

const urlTileModules: readonly UrlTileModule[] = [
    skillDetailUrlTileModule,
    jobArchitectureUrlTileModule,
    learningOpportunityUrlTileModule,
];

export const urlTileDefinitions: readonly UrlTileDefinition[] = urlTileModules.flatMap((module) => module.definitions);

export const urlTileLegacyRewriters: readonly UrlTileLegacyRewriter[] = urlTileModules.flatMap(
    (module) => module.legacyRewriters ?? [],
);

export const urlTilePermissionResolvers: TilePermissionResolvers = Object.fromEntries(
    urlTileModules.flatMap((module) => Object.entries(module.permissionResolvers ?? {})),
);

const chatUrlTileChrome = createChatUrlTileChrome({
    getChatContext: getJobArchitectureUrlTileChatContext,
    tileTypes: [
        SKILL_DETAIL_TILE_TYPE,
        ...jobArchitectureUrlTileChatTypes,
        LEARNING_OPPORTUNITY_CREATE_TILE_TYPE,
        LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE,
        LEARNING_OPPORTUNITY_EDIT_TILE_TYPE,
    ],
});

export const renderUrlTileChrome: UrlTileChromeRenderer = ({ content, context, tile }) => {
    const moduleContent = urlTileModules.reduce(
        (tileContent, module) => module.renderTileChrome?.({ content: tileContent, context, tile }) ?? tileContent,
        content,
    );

    return chatUrlTileChrome({ content: moduleContent, context, tile });
};

const renderWithUrlTileProviders = (children: ReactNode, modules: readonly UrlTileModule[]): ReactNode => {
    return modules.reduceRight((content, module) => {
        if (is.undefined(module.Provider)) {
            return content;
        }

        return createElement(module.Provider, {}, content);
    }, children);
};

export const UrlTileRegistryProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return renderWithUrlTileProviders(children, urlTileModules);
};
